<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-medium font-headline text-bhav-blue-600">Collections</h2>
    </div>

    <div class="flex w-full flex-col flex-wrap md:flex-row md:justify-start gap-4 mt-8">
        <div class="mb-4 flex-initial min-w-80">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" class="min-w-100" color="gray" placeholder="Search collections by keyword" />
        </div>
        <div class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses" all="All Statuses"/>
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
            <FormButton @click="clear" color="blue">Clear Filters</FormButton>
        </div>
        <div class="mb-4 flex-initial sm:mt-6 ml-auto">
            <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
                <p class="relative">
                    <span class="fak fa-col-options leading-none"></span>
                    <span class="sr-only">Open Table Column Preferences Drawer</span>
                </p>
            </button>
        </div>
    </div>

    <PaginationTable empty-message="There are currently no collections." :data="filtered" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false" :show-columns="showColumns" @closeDrawer="showColumns = false">
        <template #col-collection="{ row }">
            <router-link :to="{ name:'collection-show', params: { id: row.id } }" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-600">
                {{ row.meta?.info?.name || row.kova?.MarketingName }}
            </router-link>
        </template>

        <template #col-sales-status="{ row }">
            <router-link :to="{ name:'collection-show', params: { id: row.id } }" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-600">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="row.kova?.Status == 'Closed' ? 'bg-bhav-red-100 text-bhav-red-500' : 'bg-bhav-green-100 text-bhav-green-600'">
                    {{ row.kova?.CustomFields?.TextCommWebStatus1 }}
                </span>
            </router-link>
        </template>

        <template #col-status="{ row }">
            <div class="py-4 inline-flex items-center gap-1">
                <ToggleButton v-if="user.level >= 2" :id="`item-${row.id}-status`" @changed="save(row)" :checked="row.status == 'active'" 
                :label="row.status == 'active' ? 'Published' : 'Unpublished'"/>
                <component :is="user.level >= 2 ? 'label' : 'p'" :for="`item-${row.id}-status`">{{ Statuses.label(row.status) }}</component>
            </div>
        </template>
    </PaginationTable>
       
</template>

<script>
import axios from 'axios';
import {ToggleButton, SelectMultipleField, FormButton} from '@/components/form';
import PaginationTable from "@/components/PaginationTable.vue";
import Statuses from '@/enums/Statuses';
import { mapState } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import SearchInput from '../form/SearchInput.vue';

const query = {
    search: null,
    status: ['new','active','inactive']
}

export default {
    setup() {
        return {
            Statuses
        }
    },
    data() {
        return {
            query: {...query},
            showColumns: false
        }
    },
    emits: [
        'update'
    ],
    props: {
        collections: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    methods: {
        save(item) {
            if(this.user.level < 2) return;
            item.status = item.status != 'active' ? 'active' : 'inactive';
            axios.patch(`collections/${item.id}`, item)
                .then(res => {
                    if(res.data?.id) {
                        this.$emit('update',res.data);
                    }
                })
                .catch(e => {
                    throw e;
                });
        },
        clear() {
            this.query = {...query};
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        columns() {
            return [
              {
                name: 'collection',
                label: 'Collection',
                sort: (row) => row.meta?.info?.name || row.kova?.MarketingName || "",
                default: true
              },
              {
                name: 'sales-status',
                label: 'Sales Status',
                sort: (row) => row.kova?.CustomFields?.TextCommWebStatus1 || "",
                default: true
              },
              {
                name: 'status',
                label: 'Status',
                sort: (row) => Statuses.label(row.status) || "",
                default: true
              },
            ]
        },
        filtered() {
            let results = this.collections;

            if (this.query.status.length) {
                results = results.filter(item => this.query.status.includes(item.status));
            }

            if(this.query.search != '') {
                const searchTerm = this.query.search?.toLowerCase() || '';
                results = results.filter(item => {
                    const name = (item.meta?.info?.name || item.kova?.MarketingName || '').toLowerCase();
                    const description = (item.meta?.info?.description || '').toLowerCase();
                    const salesStatus = (item.kova?.CustomFields?.TextCommWebStatus1 || '').toLowerCase();

                    return name.includes(searchTerm) ||
                           description.includes(searchTerm) ||
                           salesStatus.includes(searchTerm);
                });
            }
            return results;
        },
        hasFilters() {
            return JSON.stringify(query) !== JSON.stringify(this.query);
        }
    },
    components: {
        PaginationTable,
        ToggleButton,
        SelectMultipleField,
        FormButton,
        SearchInput
    }
}
</script>